/**
 * Promotion Audit — lista promoções (awaiting/confirmed) com busca e
 * explica para cada uma quais evidências motivaram a promoção:
 *   - source_kind, raw/effective confidence, corroboration_count
 *   - is_objective (lido de person_attribute_catalog)
 *   - source_message_ids → resolvidos em syra_messages (autor, data, snippet)
 *   - timeline de eventos em syra_person_memory_audit
 *
 * Sem novos endpoints: tudo lido direto de tabelas públicas via RLS atual.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Search, ChevronDown, ChevronRight, Sparkles, Hash,
  ShieldCheck, ShieldAlert, FileText, Clock,
} from "lucide-react";

type PromotionRow = {
  id: string;
  org_id: string;
  group_id: string | null;
  subject_jid: string;
  subject_display_name: string | null;
  attribute: string;
  value: string;
  confirmation_status: string;
  source_kind: string | null;
  source_message_ids: string[] | null;
  source_author_jids: string[] | null;
  raw_confidence: number | null;
  effective_confidence: number | null;
  corroboration_count: number | null;
  metadata: any;
  created_at: string;
  updated_at: string;
};

const STATUS_TONE: Record<string, string> = {
  awaiting_confirmation: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  confirmed_by_subject: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  confirmed_by_admin: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30",
};

const REASON_LABEL: Record<string, string> = {
  a: "a — auto-declaração",
  b: "b — corroboração múltipla",
  c: "c — confiança alta + objetivo",
  d: "d — admin/manual",
  unknown: "sem motivo registrado",
};

const PERIOD_HOURS: Record<string, number> = {
  "24h": 24, "7d": 24 * 7, "30d": 24 * 30, "90d": 24 * 90,
};

export function PromotionAuditTable({
  groupId,
  groups,
}: {
  groupId: string;
  groups: { id: string; name: string }[];
}) {
  const [period, setPeriod] = useState("30d");
  const [search, setSearch] = useState("");
  const [reasonFilter, setReasonFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [expanded, setExpanded] = useState<string | null>(null);

  // ── Catálogo (is_objective por attribute) ────────────────────────────
  const { data: catalog = [] } = useQuery({
    queryKey: ["pm-catalog"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data } = await supabase
        .from("person_attribute_catalog")
        .select("attribute, is_objective, is_sensitive, category");
      return data ?? [];
    },
  });
  const catalogByAttr = useMemo(() => {
    const m: Record<string, any> = {};
    for (const c of catalog as any[]) m[c.attribute] = c;
    return m;
  }, [catalog]);

  // ── Promoções ────────────────────────────────────────────────────────
  const { data: promotions = [], isLoading } = useQuery<PromotionRow[]>({
    queryKey: ["pm-promotions", groupId, period],
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const since = new Date(
        Date.now() - PERIOD_HOURS[period] * 3600 * 1000,
      ).toISOString();
      let q = supabase
        .from("syra_person_memories")
        .select(
          "id, org_id, group_id, subject_jid, subject_display_name, attribute, value, confirmation_status, source_kind, source_message_ids, source_author_jids, raw_confidence, effective_confidence, corroboration_count, metadata, created_at, updated_at",
        )
        .in("confirmation_status", [
          "awaiting_confirmation",
          "confirmed_by_subject",
          "confirmed_by_admin",
        ])
        .gte("updated_at", since)
        .order("updated_at", { ascending: false })
        .limit(500);
      if (groupId) q = q.eq("group_id", groupId);
      const { data } = await q;
      return (data ?? []) as PromotionRow[];
    },
  });

  // ── Filtros locais ───────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return promotions.filter((p) => {
      if (reasonFilter !== "all") {
        const r = (p.metadata?.promotion_reason as string) ?? "unknown";
        if (r !== reasonFilter) return false;
      }
      if (sourceFilter !== "all" && (p.source_kind ?? "") !== sourceFilter) {
        return false;
      }
      if (!s) return true;
      const hay =
        `${p.subject_display_name ?? ""} ${p.subject_jid} ${p.attribute} ${p.value}`.toLowerCase();
      return hay.includes(s);
    });
  }, [promotions, search, reasonFilter, sourceFilter]);

  const sourceKinds = useMemo(() => {
    const s = new Set<string>();
    promotions.forEach((p) => p.source_kind && s.add(p.source_kind));
    return Array.from(s).sort();
  }, [promotions]);

  const groupNameById = useMemo(() => {
    const m: Record<string, string> = {};
    for (const g of groups) m[g.id] = g.name;
    return m;
  }, [groups]);

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Período
            </label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">Últimas 24h</SelectItem>
                <SelectItem value="7d">7 dias</SelectItem>
                <SelectItem value="30d">30 dias</SelectItem>
                <SelectItem value="90d">90 dias</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Motivo
            </label>
            <Select value={reasonFilter} onValueChange={setReasonFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(REASON_LABEL).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Origem (source_kind)
            </label>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {sourceKinds.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Busca (sujeito, atributo, valor)
            </label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ex.: aniversário, joão..."
                className="pl-8"
              />
            </div>
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-3">
          {isLoading ? "Carregando…" : `${filtered.length} promoção(ões) na janela selecionada.`}
        </div>
      </Card>

      {/* Tabela */}
      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[32px]" />
              <TableHead>Quando</TableHead>
              <TableHead>Sujeito</TableHead>
              <TableHead>Atributo · Valor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Origem</TableHead>
              <TableHead className="text-right">Confiança</TableHead>
              <TableHead className="text-right">Corrob.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                  Nenhuma promoção encontrada.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((p) => {
              const isOpen = expanded === p.id;
              const cat = catalogByAttr[p.attribute];
              const reason = (p.metadata?.promotion_reason as string) ?? "unknown";
              return (
                <Collapsible key={p.id} asChild open={isOpen} onOpenChange={(o) => setExpanded(o ? p.id : null)}>
                  <>
                    <CollapsibleTrigger asChild>
                      <TableRow className="cursor-pointer hover:bg-muted/40">
                        <TableCell>
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </TableCell>
                        <TableCell className="text-xs whitespace-nowrap">
                          {new Date(p.updated_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="font-medium">{p.subject_display_name || p.subject_jid}</div>
                          {p.group_id && (
                            <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                              {groupNameById[p.group_id] ?? p.group_id}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{p.attribute}</code>
                          <span className="ml-2 font-mono text-xs">{p.value}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={STATUS_TONE[p.confirmation_status] ?? ""}>
                            {p.confirmation_status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">
                          {p.source_kind ?? <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right text-xs font-mono">
                          {p.effective_confidence != null
                            ? p.effective_confidence.toFixed(2)
                            : p.raw_confidence != null
                              ? p.raw_confidence.toFixed(2)
                              : "—"}
                        </TableCell>
                        <TableCell className="text-right text-xs font-mono">
                          {p.corroboration_count ?? 0}
                        </TableCell>
                      </TableRow>
                    </CollapsibleTrigger>
                    <CollapsibleContent asChild>
                      <TableRow>
                        <TableCell colSpan={8} className="bg-muted/30 p-0">
                          <PromotionEvidenceDetail
                            promotion={p}
                            reason={reason}
                            isObjective={cat?.is_objective ?? null}
                            isSensitive={cat?.is_sensitive ?? null}
                            category={cat?.category ?? null}
                          />
                        </TableCell>
                      </TableRow>
                    </CollapsibleContent>
                  </>
                </Collapsible>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ── Detalhe expandido ─────────────────────────────────────────────────
function PromotionEvidenceDetail({
  promotion,
  reason,
  isObjective,
  isSensitive,
  category,
}: {
  promotion: PromotionRow;
  reason: string;
  isObjective: boolean | null;
  isSensitive: boolean | null;
  category: string | null;
}) {
  const messageIds = (promotion.source_message_ids ?? []).filter(Boolean);

  const { data: messages = [] } = useQuery({
    enabled: messageIds.length > 0,
    queryKey: ["pm-promotion-msgs", promotion.id, messageIds],
    queryFn: async () => {
      const { data } = await supabase
        .from("syra_messages")
        .select("id, content, sender_name, sender_phone, whatsapp_timestamp, created_at")
        .in("id", messageIds);
      return data ?? [];
    },
  });

  const { data: audit = [] } = useQuery({
    queryKey: ["pm-promotion-audit", promotion.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("syra_person_memory_audit")
        .select("event_type, from_state, to_state, actor_kind, actor_id, payload, created_at")
        .eq("memory_id", promotion.id)
        .order("created_at", { ascending: false })
        .limit(50);
      return data ?? [];
    },
  });

  const promotedAt = promotion.metadata?.promoted_at as string | undefined;
  const promotionRule = promotion.metadata?.promotion_rule as string | undefined;
  const promotionDetails = promotion.metadata?.promotion_details;

  return (
    <div className="p-4 space-y-4">
      {/* Why-promoted */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-3">
          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Motivo da promoção
          </div>
          <div className="text-sm font-medium">{REASON_LABEL[reason] ?? reason}</div>
          {promotionRule && (
            <div className="text-xs text-muted-foreground mt-1">
              regra: <code>{promotionRule}</code>
            </div>
          )}
          {promotedAt && (
            <div className="text-xs text-muted-foreground mt-0.5">
              em {new Date(promotedAt).toLocaleString()}
            </div>
          )}
        </Card>

        <Card className="p-3">
          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            {isObjective ? (
              <ShieldCheck className="w-3 h-3 text-emerald-500" />
            ) : (
              <ShieldAlert className="w-3 h-3 text-amber-500" />
            )}{" "}
            Tipo do atributo
          </div>
          <div className="text-sm font-medium">
            {isObjective == null ? "—" : isObjective ? "Objetivo" : "Subjetivo"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            categoria: {category ?? "—"}
            {isSensitive ? " · sensível" : ""}
          </div>
        </Card>

        <Card className="p-3">
          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Hash className="w-3 h-3" /> Sinais
          </div>
          <div className="text-xs space-y-0.5">
            <div>
              source_kind: <code>{promotion.source_kind ?? "—"}</code>
            </div>
            <div>
              raw: <code>{promotion.raw_confidence?.toFixed(3) ?? "—"}</code>
              {" · "}
              effective: <code>{promotion.effective_confidence?.toFixed(3) ?? "—"}</code>
            </div>
            <div>
              corroborações: <code>{promotion.corroboration_count ?? 0}</code>
              {" · "}
              autores únicos:{" "}
              <code>
                {(promotion.source_author_jids ?? []).filter(Boolean).length}
              </code>
            </div>
          </div>
        </Card>
      </div>

      {/* Detalhes brutos da promoção (se houver) */}
      {promotionDetails && (
        <Card className="p-3">
          <div className="text-xs text-muted-foreground mb-2">
            Detalhes da promoção (metadata.promotion_details)
          </div>
          <pre className="text-xs bg-background p-2 rounded border overflow-x-auto">
            {JSON.stringify(promotionDetails, null, 2)}
          </pre>
        </Card>
      )}

      {/* Mensagens-fonte */}
      <Card className="p-3">
        <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
          <FileText className="w-3 h-3" /> Mensagens-fonte ({messageIds.length})
        </div>
        {messageIds.length === 0 && (
          <div className="text-xs text-muted-foreground">
            Nenhum source_message_id registrado.
          </div>
        )}
        <div className="space-y-2">
          {messages.map((m: any) => (
            <div key={m.id} className="text-xs border-l-2 border-primary/40 pl-3 py-1">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{m.sender_name || m.sender_phone || "—"}</span>
                <span className="text-muted-foreground">
                  {new Date(m.whatsapp_timestamp ?? m.created_at).toLocaleString()}
                </span>
              </div>
              <div className="text-muted-foreground whitespace-pre-wrap break-words mt-0.5">
                {m.content?.slice(0, 320) || <em>(sem texto)</em>}
                {m.content && m.content.length > 320 ? "…" : ""}
              </div>
            </div>
          ))}
          {/* IDs sem hit em syra_messages */}
          {messages.length < messageIds.length && (
            <div className="text-xs text-muted-foreground italic">
              {messageIds.length - messages.length} ID(s) não encontrado(s) em syra_messages
              (provavelmente fora da janela de retenção).
            </div>
          )}
        </div>
      </Card>

      {/* Timeline */}
      <Card className="p-3">
        <div className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
          <Clock className="w-3 h-3" /> Timeline ({audit.length})
        </div>
        <div className="space-y-1">
          {audit.map((a: any, i: number) => (
            <div key={i} className="text-xs flex items-start gap-2 py-1">
              <span className="text-muted-foreground whitespace-nowrap">
                {new Date(a.created_at).toLocaleString()}
              </span>
              <Badge variant="outline" className="text-[10px]">{a.event_type}</Badge>
              {a.from_state && a.to_state && (
                <span className="text-muted-foreground">
                  {a.from_state} → <b>{a.to_state}</b>
                </span>
              )}
              <span className="text-muted-foreground">· {a.actor_kind}</span>
              {a.payload && Object.keys(a.payload).length > 0 && (
                <code className="text-[10px] bg-muted px-1 rounded">
                  {JSON.stringify(a.payload)}
                </code>
              )}
            </div>
          ))}
          {audit.length === 0 && (
            <div className="text-xs text-muted-foreground">Sem eventos.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
